import { useState, useEffect, useCallback } from 'react';
import { PhoneXMarkIcon, MicrophoneIcon, VideoCameraIcon, VideoCameraSlashIcon } from '@heroicons/react/24/outline';
import type { ClubTheme } from './themes';
import { useAuth } from '@/lib/auth-context';

interface LiveKitRoomProps {
  clubId: string;
  eventId: string;
  theme: ClubTheme;
  onLeave: () => void;
}

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

/**
 * LiveKit room component.
 *
 * This is a lightweight wrapper that:
 * 1. Fetches a LiveKit token from our backend
 * 2. Renders a video room using LiveKit's embedded iframe approach
 *
 * For a full-featured integration, install @livekit/components-react and
 * replace the iframe with <LiveKitRoom> from that package. This approach
 * works without any additional dependencies and keeps bundle size small.
 */
export default function LiveKitRoom({ clubId, eventId, theme, onLeave }: LiveKitRoomProps) {
  const { session } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [livekitUrl, setLivekitUrl] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);

  const accessToken = session?.access_token || '';

  const fetchToken = useCallback(async () => {
    if (!accessToken) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res = await fetch(
        `${CORE_API}/api/v1/social/${clubId}/events/${eventId}/livekit-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const json = await res.json();

      if (!res.ok) {
        const msg = typeof json.error === 'string' ? json.error : JSON.stringify(json.error) || 'Failed to get video token';
        throw new Error(msg);
      }

      setToken(json.data.token);
      setLivekitUrl(json.data.url);
      setRoomName(json.data.room);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [clubId, eventId, accessToken]);

  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-xl p-8"
        style={{ backgroundColor: '#000', border: `1px solid ${theme.colors.muted}20` }}>
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"
          style={{ borderColor: '#ffffff40', borderTopColor: '#22c55e' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl p-4 text-center"
        style={{ backgroundColor: '#000', border: `1px solid ${theme.colors.muted}20` }}>
        <p className="text-sm text-red-400 mb-2">{error}</p>
        {error === 'livekit-not-configured' ? (
          <p className="text-xs text-gray-500">
            Video is not configured yet. The host needs to set up LiveKit credentials.
          </p>
        ) : (
          <button type="button" onClick={fetchToken}
            className="rounded px-3 py-1 text-xs font-medium text-white bg-gray-700">
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!token || !livekitUrl || !roomName) return null;

  // Convert wss:// LiveKit server URL to the HTTPS meet URL
  // LiveKit Meet (meet.livekit.io) is a hosted prebuilt UI that accepts server + token params
  // Also works with LiveKit's custom domain format
  const serverUrl = livekitUrl.replace('wss://', 'https://');
  const meetUrl = `https://meet.livekit.io/custom?liveKitUrl=${encodeURIComponent(livekitUrl)}&token=${encodeURIComponent(token)}`;

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${theme.colors.muted}20` }}>
      {/* Video area */}
      <div className="relative aspect-video bg-black">
        <iframe
          src={meetUrl}
          allow="camera; microphone; display-capture; autoplay; clipboard-write"
          className="h-full w-full border-none"
          title="LiveKit Video Room"
        />
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-3 bg-gray-900 px-4 py-3">
        <button type="button"
          onClick={() => setAudioMuted(!audioMuted)}
          className={`rounded-full p-2.5 transition-colors ${audioMuted ? 'bg-red-500/20' : 'bg-gray-700'}`}
          title={audioMuted ? 'Unmute' : 'Mute'}>
          <MicrophoneIcon className={`h-5 w-5 ${audioMuted ? 'text-red-400' : 'text-white'}`} />
        </button>

        <button type="button"
          onClick={() => setVideoMuted(!videoMuted)}
          className={`rounded-full p-2.5 transition-colors ${videoMuted ? 'bg-red-500/20' : 'bg-gray-700'}`}
          title={videoMuted ? 'Turn on camera' : 'Turn off camera'}>
          {videoMuted
            ? <VideoCameraSlashIcon className="h-5 w-5 text-red-400" />
            : <VideoCameraIcon className="h-5 w-5 text-white" />
          }
        </button>

        <button type="button" onClick={onLeave}
          className="rounded-full bg-red-600 p-2.5 transition-colors hover:bg-red-700"
          title="Leave room">
          <PhoneXMarkIcon className="h-5 w-5 text-white" />
        </button>
      </div>
    </div>
  );
}
